import React, { RefCallback } from "react";
import { Typography } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { InputWithRef } from "@/components/forms/inputsWithRef/inputWithRef";
import CrudFieldController from "@/components/tables/fields/CrudFieldController";
import { isInputBlockedByRule } from "@/components/tables/fields/fieldRules";
import {
  buildTextFieldRules,
  getEmailFieldState,
  isTriggerClassNameRequired,
} from "@/components/tables/fields/textFieldRules";
import {
  toPriceDisplayValue,
  toPriceFormValue,
} from "@/components/tables/fields/priceFieldValue";
import {
  ANALOG_FLAG_FIELD_NAME,
  isPriceField,
  REGISTERED_FLAG_FIELD_NAME,
  SCHEDULED_FIELD_NAME,
} from "@/components/tables/fields/fieldNameRules";
import { composeInputRef } from "@/components/tables/fields/validationTarget";
import type { CrudModalField } from "@/components/tables/fields/crudModalField";
import styles from "@/components/tables/fields/crudField.module.css";

export interface CrudTextFieldProps {
  field: Pick<
    CrudModalField,
    | "id"
    | "header"
    | "required"
    | "errorMessage"
    | "rule"
    | "errorMessageValidation"
    | "maxLength"
    | "maxLengthMessage"
    | "validate"
    | "blockInvalidInput"
    | "textfieldType"
    | "inputMode"
    | "autoComplete"
    | "helperText"
    | "hideField"
  >;
  entityOfTable?: string;
  convertsPriceToEurocent?: boolean;
  modalMode?: string;
  userEmails?: string[];
  registerValidationTarget?: (name: string) => RefCallback<HTMLElement>;
  onChange?: (value: any) => void;
}

const CrudTextField = ({
  field,
  entityOfTable,
  convertsPriceToEurocent = true,
  modalMode,
  userEmails,
  registerValidationTarget,
  onChange,
}: CrudTextFieldProps) => {
  const name = field.id as string;
  const { t } = useTranslation();
  const { control, getValues } = useFormContext();
  const analogFlag = useWatch({ control, name: ANALOG_FLAG_FIELD_NAME });
  const scheduledFlag = useWatch({ control, name: SCHEDULED_FIELD_NAME });

  if (field.hideField) return null;

  const subdomain = window.location.hostname.split(".")[0];
  const email = getEmailFieldState(field.id, {
    entityOfTable,
    analogFlag,
    subdomain,
    modalMode,
    isRegistered: getValues(REGISTERED_FLAG_FIELD_NAME),
  });
  const convertPrice = isPriceField(field.id) && convertsPriceToEurocent;
  const helperText = email.generatedForAnalogUser
    ? t("email_will_be_generated_for_analog_users")
    : email.lockedForRegisteredUser
    ? t("email_no_edit")
    : field.helperText;

  return (
    <div>
      <CrudFieldController
        name={name}
        control={control}
        rules={buildTextFieldRules(
          field,
          {
            email,
            triggerClassNameRequired: isTriggerClassNameRequired(
              entityOfTable,
              scheduledFlag
            ),
            modalMode,
            userEmails,
          },
          t
        )}
        render={({
          field: { onChange: setFormValue, value, ...rest },
          error,
        }) => (
          <div className={styles.textField}>
            <InputWithRef
              {...rest}
              ref={composeInputRef(
                rest.ref,
                registerValidationTarget?.(rest.name)
              )}
              label={field.header}
              required={field.required}
              htmlType={field.textfieldType}
              inputMode={field.inputMode}
              name={field.id}
              onChange={(event: any) => {
                const typed = event.target.value;
                if (isInputBlockedByRule(field, typed)) return;
                const nextValue = isPriceField(field.id)
                  ? toPriceFormValue(typed, convertPrice)
                  : typed;
                onChange?.(nextValue);
                setFormValue(nextValue);
              }}
              value={
                convertPrice
                  ? toPriceDisplayValue(value ?? "", true)
                  : value ?? ""
              }
              inputClassname={error ? styles.inputError : undefined}
              autoComplete={field.autoComplete}
              onWheel={
                field.textfieldType === "number"
                  ? (event: React.WheelEvent<HTMLInputElement>) =>
                      event.currentTarget.blur()
                  : undefined
              }
              disabled={
                email.lockedForRegisteredUser || email.generatedForAnalogUser
              }
            />
            {helperText && (
              <Typography className={styles.helperText}>
                {helperText}
              </Typography>
            )}
          </div>
        )}
      />
    </div>
  );
};

export default CrudTextField;
